import { TPricingPeriod } from '~/components/Membership/types/membership.types';
import { IReferrerMarketingResponse } from '~/types/server.types';
import type { UserData } from '~/components/Signup/utils/signup-form.utils';
import { ReactNode, RefObject } from 'react';
import type { ValidationSchema } from '~/components/Signup/hooks/useStepValidationSchema';
import { IUseSignupStepsReturn } from '~/components/Signup/hooks/useSignupSteps';
import { UserService } from '~/services/user.service';
import {
    TOptionValue,
    IOption
} from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown.types';
import { FormikHelpers } from 'formik';
import { JStorageService } from '~/services/j-storage.service';

export type FirebaseProviderType = 'google' | 'apple';

export enum IntakeDesign {
    Old = 'old design-03/2023',
    Rebrand = 'rebrand design-03/2023'
}

export type TSource =
    | 'hero'
    | 'starter'
    | 'essentials'
    | 'premium'
    | 'pic_time'
    | 'pic_time_mobile'
    | 'freelancers_union'
    | 'john_branch'
    | 'referrals'
    | 'default'
    | 'public_template'
    | 'adobe'
    | 'adobe_new'
    | 'breakthrough_grant'
    | 'prismm'
    | 'prismm_mobile'
    | 'instabooked_msp_invite'
    | 'zoom_mobile'
    | 'timeline_genius'
    | 'timeline_genius_mobile'
    | 'template'
    | 'weven'
    | 'weven_mobile'
    | 'email_only'
    | 'default_30d';

export type BuyNowData = { period?: TPricingPeriod; source?: TSource };

export const enum SignupPageLayout {
    Original = 'original',
    Blur = 'blur',
    JohnBranch = 'john_branch',
    FreelancersUnion = 'freelancers_union',
    Adobe = 'adobe',
    BreakthroughGrantNurtureV1 = 'breakthrough_grant_nurture_v1',
    Referrals = 'referrals',
    Zoom = 'zoom',
    ClientRelationships = 'client_relationships'
}

export type TSSOType =
    | 'google'
    | 'google_one_tap'
    | 'facebook'
    | 'apple'
    | 'regular';

export type TReferralData = IReferrerMarketingResponse;

export interface AnalyticsProps {
    name: string;
    email: string;
    orig_vendor_email: string;
    registration_variant: string;
    is_demo: string;
    signup_landing_page: string;
    sso_type: TSSOType;
    source: string;
    variant: string;
    company_name: string;
    company_type: string;
    company_sub_type: string;
    company_other: string;
    phone_number: string;
    how_heard_of_us: string;
    is_multiple_stage_intake_form: string;
    error_status: string;
    error_type: string;
    error_message: string;
    intake_design?: string;
    template_id?: string;
    source_template_id?: string;
    referral_recipient_variant?: 'control' | 'recipient';
    website_url?: string;
    template_type?: string;
    company_type_other?: string;
    generated_template_type?: string;
    broad_intent_get_started_hero?: string;
    intake_variant?: string;
}

export interface ImportPublicTemplateParsedValue {
    templateId: string;
    isGalleryTemplate: string;
    templateRedeemCode?: string;
    importedTemplateId?: string;
}

export type IOnetabResponse = google.accounts.id.CredentialResponse & {
    name: string;
    email: string;
    picture?: string;
};

export interface IGISInfo {
    displayName?: string;
    email: string;
    photoURL?: string;
}

export interface IGoogleJWTPayload {
    name?: string; // Full name
    email?: string; // Email address
    picture?: string; // Profile picture URL
    given_name?: string; // First name
    family_name?: string; // Last name
}

export interface TtrackSignupFormSubmit {
    formValues: TSignupFormValues;
    isSSO?: boolean;
    isOneTap?: boolean;
}

export interface ITrackSignupForm {
    trackFormSubmit: (props: TtrackSignupFormSubmit) => void;
    trackErrorSubmit: (props: TtrackSignupFormSubmit) => void;
    trackModalStep: (props: { formikSSO: TSSOType }) => void;
    trackIntakeFormModalLoaded: (props: { formikSSO: TSSOType }) => void;
    trackSSOUserAlreadyExists: (props: {
        email: string;
        name: string;
        ssoType: TSSOType;
    }) => void;
    trackSSOSignupError: (error: string) => void;
    trackSSOApprovalRequest: (ssoType: TSSOType) => void;
    trackReferralRecipientExperience: () => void;
}

/*
    ISignupUserData
    - Used to normalize data from marketing LPs
*/
export interface ISignupUserData {
    How_heard_of_us__c?: string;
    company_name?: string;
    company_other?: string;
    company_type?: string;
    company_sub_type?: string;
    email?: string;
    vendor_name?: string;
    vendor_email?: string;
    name?: string;
    password?: string;
    phone_number?: string;
    auth_token?: string;
    sso_type?: TSSOType;
    auth_code?: string;
    is_sso?: boolean;
    is_firebase?: string;
    profile_img_url?: string;
    website_url?: string;
    is_nev?: boolean;
    referrer?: string;
}

export interface IMarketingSignupData {
    start_trial_variant?: string;
    signupLandingPage?: string;
    signup_landing_page?: string;
    registration_variant?: string;
    optimize_experiments?: string;
    is_demo?: boolean;
    source?: string;
    couponCode?: string;
    code?: string;
    promo?: string;
    utmMbsy?: string;
    utm_campaign?: string;
    utm_medium?: string;
    utm_source?: string;
    utm_customer?: string;
    userData?: ISignupUserData & { picture?: string };
    dataForIntakeFormTest?: {
        buyNowData: BuyNowData;
        taxToolData: { email: string };
        personalized_template?: {
            companyType: string;
            templateType: string;
        };
    };
    ab_tests?: any;
}

/*
    Signup Form Steps
*/
interface IFormCredentialsStep {
    name: string;
    email: string;
    password: string;
    websiteUrl?: string;
}

interface IFormCompanyInfoStep {
    companyName: string;
    companyType: string;
    companySubType?: string;
    companyTypeOther?: string;
    phoneNumber: string;
    howDidYouHeadAboutUs?: string;
    terms?: boolean;
    websiteUrl?: string;
}

type TSignupFormikValues = IFormCredentialsStep & IFormCompanyInfoStep;

/*
    Single Sign-On (SSO)
*/
interface ISSOAuthData {
    auth_token: string;
    sso_type: TSSOType;
    auth_code: string;
    is_sso: boolean;
    is_firebase: boolean;
    profile_img_url?: string;
}

export type TSignupFormValues = TSignupFormikValues & Partial<ISSOAuthData>;

/*
    ISignupInit
    - Methods and data returned from useSignupInit
*/
export interface ISignupInit {
    marketingSiteData: IMarketingSignupData;
    handleCreateUser: (values: TSignupFormValues) => void;
    handleValidateEmail: (
        userData: UserData
    ) => Promise<{ isValid: boolean; redirectToUrl?: string }>;
    handleTrackSignupForm: ITrackSignupForm;
    validationSchema: ValidationSchema;
    signupSteps: IUseSignupStepsReturn;
    stepIndex: number;
    setGeneralServerError: (error: string) => void;
    setIsRedirectedFromSSO: (isRedirected: boolean) => void;
    isRedirectedFromSSO: boolean;
    setIsWaitingForSSOResponse: (isWaiting: boolean) => void;
    setIsOneTap: (isOneTap: boolean) => void;
    userService: UserService;
    jStorageService: JStorageService;
}

export interface ISignupFormProvider {
    children: ReactNode;
    signupInit: ISignupInit;
}

export interface IUseSignupFormProps {
    onCloseModal?: () => void;
    signupInit: ISignupInit;
}

/*
    CompanyInfoStepFormFieldsProps
    Sigup form fields for company info step
*/
type OptionsArray = IOption<TOptionValue>[];

export interface CompanyInfoStepFormFieldsProps {
    t: (key: string) => string;
    hideHDYHAU: boolean;
    hideCompanyTypeDropdown?: boolean;
    filterCompanyTypeOption: (option: IOption, rawInput: string) => boolean;
    onCreateCompanyType: (value: string) => void;
    onCompanyTypeChanged: (value: TOptionValue) => void;
    onCompanySubTypeChanged: (value: string) => void;
    onSubTypeDropDownOpen: () => void;
    onSetSelectedHDYHAU: (value: string) => void;
    companyTypes: OptionsArray;
    companySubTypeOptions: OptionsArray;
    howDidYouHearAboutUsOptions: OptionsArray;
    selectedCompanyType: string | null;
    selectedCompanySubType: string | null;
    selectedHDYHAU: string;
    companyOtherInputRef: RefObject<HTMLInputElement>;
    companySubTypesDropDownRef: RefObject<HTMLSelectElement>;
    phoneNumberInputRef: RefObject<HTMLInputElement>;
    hasCompanySubTypes: boolean;
    shouldPrefillValues: (
        setFieldValue: FormikHelpers<any>['setFieldValue']
    ) => void;
}
